/**
 * Tabla virtualizada del Action Center Premium (0666b).
 *
 * Usa @tanstack/react-virtual: ~36px row height · sticky header · 100 filas
 * en viewport · resto virtualizadas. Performante hasta ~10k filas sin lag.
 */

import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useI18n } from '../../../../i18n';
import { ActionCenterRow, ROW_GRID_COLS } from './ActionCenterRow';
import type { ActionItem } from '../hooks/useActionCenterList';

const ROW_HEIGHT = 44;
const HEADER_HEIGHT = 38;

export interface ActionCenterTableProps {
  items: ActionItem[];
  selectedId: string | null;
  onSelect: (item: ActionItem) => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}/${mm}/${yy}`;
}

export function ActionCenterTable(props: ActionCenterTableProps) {
  const { t } = useI18n();
  const tt = t.sign.actionCenter.premium;
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: props.items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  if (props.isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 220,
          color: '#6B7280',
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 14,
        }}
      >
        {tt.loading}
      </div>
    );
  }

  if (props.isError) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 220,
          gap: 12,
          fontFamily: 'DM Sans, sans-serif',
        }}
      >
        <span style={{ color: '#991B1B', fontSize: 14, fontWeight: 600 }}>{tt.errorTitle}</span>
        <button
          type="button"
          onClick={props.onRetry}
          style={{
            padding: '6px 16px',
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            color: '#fff',
            background: '#1F3864',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          {tt.errorRetry}
        </button>
      </div>
    );
  }

  if (props.items.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 220,
          gap: 6,
          fontFamily: 'DM Sans, sans-serif',
        }}
      >
        <span style={{ color: '#1F3864', fontSize: 16, fontWeight: 600 }}>{tt.emptyTitle}</span>
        <span style={{ color: '#6B7280', fontSize: 13 }}>{tt.emptySubtitle}</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 320 }}>
      {/* Sticky header — alineado con ActionCenterRow.ROW_GRID_COLS (6 cols). */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: ROW_GRID_COLS,
          gap: 16,
          alignItems: 'center',
          padding: '0 20px',
          height: HEADER_HEIGHT,
          background: '#F9FAFB',
          borderBottom: '1px solid #E5E7EB',
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 10,
          fontWeight: 700,
          color: '#6B7280',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}
      >
        <span>{tt.colTipo}</span>
        <span>{tt.colAsunto}</span>
        <span>{tt.colCodigo}</span>
        <span>{tt.colSolicitante}</span>
        <span>{tt.colFecha}</span>
        <span>{tt.colEstado}</span>
      </div>

      {/* Body virtualizado */}
      <div
        ref={parentRef}
        style={{
          flex: 1,
          overflow: 'auto',
          background: '#fff',
        }}
      >
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((vRow) => {
            const item = props.items[vRow.index];
            if (!item) return null;
            return (
              <div
                key={item.id}
                data-action-row-id={item.id}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${vRow.start}px)`,
                }}
              >
                <ActionCenterRow
                  item={item}
                  isSelected={props.selectedId === item.id}
                  onSelect={props.onSelect}
                  formatDate={formatDate}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
